"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _classnames = _interopRequireDefault(require("classnames"));
var _get = _interopRequireDefault(require("lodash/get"));
var _components = require("@folio/stripes/components");
var _SearchField = _interopRequireDefault(require("../SearchField"));
var _TypeDown = _interopRequireDefault(require("../../../styles/TypeDown.css"));
var _typedownHooks = require("../hooks/typedownHooks");
var _selectorSafe = _interopRequireDefault(require("../utils/selectorSafe"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const Typedown = _ref => {
  let {
    className,
    dataOptions,
    displayClearItem = true,
    endOfList,
    id,
    input,
    isSelected,
    filterPath,
    label,
    meta,
    onChange,
    onType,
    renderFooter = null,
    renderListItem = null,
    required,
    selectedStyles,
    // A way to pass any styles that need to be applied globally on selection
    uniqueIdentificationPath = 'id'
  } = _ref;
  const selectedUniqueId = (0, _get.default)(input.value, uniqueIdentificationPath);

  // Display data needs to be in line with data options but also able to react to default handleType
  const [displayData, setDisplayData] = (0, _react.useState)(dataOptions);

  // keep track of what we've typed and whether we've typed an exact match or not
  const [currentlyTyped, setCurrentlyTyped] = (0, _react.useState)('');
  const [exactMatch, setExactMatch] = (0, _react.useState)(false);
  (0, _react.useEffect)(() => {
    setDisplayData(dataOptions);
  }, [dataOptions]);

  // Setup default handleType
  const handleType = e => {
    const regex = new RegExp(`${e.target.value.toLowerCase()}`);
    if (onType) {
      onType(e);
    } else if (filterPath && e?.target?.value) {
      setDisplayData(dataOptions.filter(item => (0, _get.default)(item, filterPath)?.toLowerCase()?.match(regex)));
    } else if (e?.target?.value) {
      setDisplayData(dataOptions.filter(item => (0, _get.default)(item, uniqueIdentificationPath)?.toLowerCase()?.match(regex)));
    } else {
      setDisplayData(dataOptions);
    }
    setCurrentlyTyped(e.target.value);
    if (displayData.length === 1 && (0, _get.default)(displayData[0], filterPath) === e.target.value) {
      setExactMatch(true);
    } else {
      setExactMatch(false);
    }
  };

  // Hook to set up all the essentials
  const {
    refs: {
      listRef,
      triggerRef,
      overlayRef,
      footerRef
    },
    handlers: {
      handleNextFocus,
      listKeyDownHandler,
      searchFieldKeyDownHandler
    },
    variables: {
      open,
      portal,
      resizeRef,
      searchWidth
    }
  } = (0, _typedownHooks.useTypedown)(input.name);
  const renderItem = (0, _react.useCallback)(function (option) {
    let optionIsSelected = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      className: _TypeDown.default.listItem,
      children: renderListItem ? renderListItem(option, currentlyTyped, exactMatch, optionIsSelected) : (0, _get.default)(option, uniqueIdentificationPath)
    });
  }, [currentlyTyped, exactMatch, renderListItem, uniqueIdentificationPath]);
  const handleChange = (0, _react.useCallback)(value => {
    input.onChange(value);
    if (typeof onChange === 'function') {
      onChange(value);
    }
  }, [input, onChange]);
  const dropDown = (0, _react.useCallback)(() => {
    return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: _TypeDown.default.dropdownMenu,
      id: `typedown-parent-${(0, _selectorSafe.default)(input.name)}-menu`,
      style: {
        '--searchWidth': `${searchWidth}px`
      },
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        ref: listRef,
        className: _TypeDown.default.listContainer,
        id: "typedown-list",
        children: displayData?.length ? displayData?.map((d, index) => {
          const isSelectedEval = isSelected ? isSelected(input.value, d) : (0, _get.default)(input.value, uniqueIdentificationPath) === (0, _get.default)(d, uniqueIdentificationPath);
          const selectedCSS = selectedStyles ?? _TypeDown.default.selectedMenuButton;
          return /*#__PURE__*/(0, _jsxRuntime.jsx)("button", {
            className: (0, _classnames.default)(_TypeDown.default.fullWidth, _TypeDown.default.menuButton, {
              [`${selectedCSS}`]: isSelectedEval
            }),
            "data-selected": isSelectedEval,
            id: `typedown-button-[${index}]`,
            onClick: () => {
              handleChange(d);
              handleNextFocus();
            },
            onKeyDown: listKeyDownHandler,
            type: "button",
            children: renderItem(d, isSelectedEval)
          }, `typedown-button-[${index}]`);
        }) : endOfList || /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.EndOfList, {})
      }), renderFooter && /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        ref: footerRef,
        className: _TypeDown.default.footer,
        id: `typedown-footer-${(0, _selectorSafe.default)(input.name)}`,
        children: renderFooter(displayData, currentlyTyped, exactMatch)
      })]
    });
  }, [currentlyTyped, displayData, endOfList, exactMatch, footerRef, handleChange, handleNextFocus, input.name, input.value, isSelected, listKeyDownHandler, listRef, renderFooter, renderItem, searchWidth, selectedStyles, uniqueIdentificationPath]);
  const renderSearchField = () => {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
      ref: triggerRef,
      id: `typedown-parent-${(0, _selectorSafe.default)(input.name)}-searchField`,
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_SearchField.default
      // Pass meta through so correct styling gets applied to the TextField
      , {
        id: `typedown-searchField-${(0, _selectorSafe.default)(input.name)}`,
        label: label,
        marginBottom0: true,
        meta: meta,
        onChange: handleType,
        onKeyDown: searchFieldKeyDownHandler,
        required: required
      })
    });
  };
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
    ref: resizeRef,
    className: (0, _classnames.default)(_TypeDown.default.typedown, className),
    id: `typedown-id-${id}`,
    children: [renderSearchField(), /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.Popper, {
      anchorRef: triggerRef,
      className: (0, _classnames.default)(_TypeDown.default.dropdown, _TypeDown.default.fullWidth),
      isOpen: open,
      modifiers: {
        flip: {
          boundariesElement: 'viewport',
          padding: 10
        },
        preventOverflow: {
          boundariesElement: 'viewport',
          padding: 10
        }
      },
      overlayProps: {
        'ref': overlayRef,
        'tabIndex': '-1',
        'onClick': e => {
          e.stopPropagation();
        } // prevent propagation of click events
      },
      overlayRef: overlayRef,
      portal: portal,
      children: dropDown()
    }, "typedown-menu-toggle"), selectedUniqueId && !open && /*#__PURE__*/(0, _jsxRuntime.jsxs)("div", {
      className: (0, _classnames.default)(_TypeDown.default.selectedDisplay),
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)("div", {
        className: _TypeDown.default.selectedItem,
        children: renderItem(input.value)
      }), displayClearItem && /*#__PURE__*/(0, _jsxRuntime.jsx)(_components.IconButton, {
        className: _TypeDown.default.clearItem,
        icon: "times-circle-solid",
        onClick: () => handleChange()
      })]
    })]
  });
};
Typedown.propTypes = {
  className: _propTypes.default.string,
  dataOptions: _propTypes.default.arrayOf(_propTypes.default.object),
  displayClearItem: _propTypes.default.bool,
  endOfList: _propTypes.default.oneOfType([_propTypes.default.func, _propTypes.default.node, _propTypes.default.element]),
  filterPath: _propTypes.default.string,
  id: _propTypes.default.string,
  input: _propTypes.default.object,
  isSelected: _propTypes.default.func,
  label: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.element]),
  meta: _propTypes.default.object,
  onChange: _propTypes.default.func,
  onType: _propTypes.default.func,
  renderFooter: _propTypes.default.func,
  renderListItem: _propTypes.default.func,
  required: _propTypes.default.bool,
  selectedStyles: _propTypes.default.string,
  uniqueIdentificationPath: _propTypes.default.string
};
var _default = exports.default = Typedown;